import React, { useState, useMemo } from 'react'
import { IoWarningOutline, IoChevronDown, IoChevronUp } from 'react-icons/io5'
import type { PlayersCardTableHub, PlayerCardRowHub } from '../../../types'

interface PlayerCardsPanelProps {
  players: PlayersCardTableHub
}

type SortKey = 'card_risk_score' | 'yc_per_90' | 'fouls_per_90' | 'name' | 'suspension_risk'
type SortOrder = 'asc' | 'desc'

export function PlayerCardsPanel({ players }: PlayerCardsPanelProps) {
  const [showTeam, setShowTeam] = useState<'both' | 'home' | 'away'>('both')
  const [sortKey, setSortKey] = useState<SortKey>('card_risk_score')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
  const [expandedPlayer, setExpandedPlayer] = useState<number | null>(null)

  // Combine and sort players
  const sortedPlayers = useMemo(() => {
    let playerList: (PlayerCardRowHub & { isHome: boolean })[] = []

    if (showTeam === 'both' || showTeam === 'home') {
      playerList.push(...players.home_players.map((p) => ({ ...p, isHome: true })))
    }
    if (showTeam === 'both' || showTeam === 'away') {
      playerList.push(...players.away_players.map((p) => ({ ...p, isHome: false })))
    }

    // Sort
    playerList.sort((a, b) => {
      let aVal: number | string = 0
      let bVal: number | string = 0

      switch (sortKey) {
        case 'card_risk_score':
          aVal = a.card_risk_score
          bVal = b.card_risk_score
          break
        case 'yc_per_90':
          aVal = a.yc_per_90
          bVal = b.yc_per_90
          break
        case 'fouls_per_90':
          aVal = a.fouls_per_90 ?? 0
          bVal = b.fouls_per_90 ?? 0
          break
        case 'name':
          aVal = a.name.toLowerCase()
          bVal = b.name.toLowerCase()
          break
        case 'suspension_risk':
          aVal = a.is_suspension_risk ? 1 : 0
          bVal = b.is_suspension_risk ? 1 : 0
          break
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }

      return sortOrder === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number)
    })

    return playerList
  }, [players, showTeam, sortKey, sortOrder])

  // Handle sort header click
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortOrder('desc')
    }
  }

  // Get risk level color
  const getRiskColor = (score: number) => {
    if (score >= 0.7) return 'var(--color-danger)'
    if (score >= 0.4) return 'var(--color-warning)'
    return 'var(--color-success)'
  }

  // Sort indicator
  const SortIndicator = ({ column }: { column: SortKey }) => {
    if (sortKey !== column) return null
    return sortOrder === 'asc' ? <IoChevronUp size={12} /> : <IoChevronDown size={12} />
  }

  return (
    <div className="cards-panel player-cards-panel">
      {/* Lineup Status */}
      {!players.lineup_confirmed && (
        <div className="cards-warning-banner">
          <IoWarningOutline size={16} />
          <span>Lineups not confirmed - using predicted starters</span>
        </div>
      )}

      {/* Team Filter */}
      <div className="cards-window-selector">
        <button
          type="button"
          className={`chip ${showTeam === 'both' ? 'chip-active' : ''}`}
          onClick={() => setShowTeam('both')}
        >
          Both Teams
        </button>
        <button
          type="button"
          className={`chip ${showTeam === 'home' ? 'chip-active' : ''}`}
          onClick={() => setShowTeam('home')}
        >
          Home
        </button>
        <button
          type="button"
          className={`chip ${showTeam === 'away' ? 'chip-active' : ''}`}
          onClick={() => setShowTeam('away')}
        >
          Away
        </button>
      </div>

      {/* Player Table */}
      <div className="player-cards-table-wrapper">
        <table className="player-cards-table">
          <thead>
            <tr>
              <th
                className={`sortable ${sortKey === 'name' ? 'sorted' : ''}`}
                onClick={() => handleSort('name')}
              >
                Player <SortIndicator column="name" />
              </th>
              <th>Pos</th>
              <th
                className={`sortable ${sortKey === 'card_risk_score' ? 'sorted' : ''}`}
                onClick={() => handleSort('card_risk_score')}
              >
                Risk <SortIndicator column="card_risk_score" />
              </th>
              <th
                className={`sortable ${sortKey === 'yc_per_90' ? 'sorted' : ''}`}
                onClick={() => handleSort('yc_per_90')}
              >
                YC/90 <SortIndicator column="yc_per_90" />
              </th>
              <th
                className={`sortable ${sortKey === 'fouls_per_90' ? 'sorted' : ''}`}
                onClick={() => handleSort('fouls_per_90')}
              >
                Fouls/90 <SortIndicator column="fouls_per_90" />
              </th>
              <th
                className={`sortable ${sortKey === 'suspension_risk' ? 'sorted' : ''}`}
                onClick={() => handleSort('suspension_risk')}
              >
                Susp. <SortIndicator column="suspension_risk" />
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedPlayers.map((player) => (
              <React.Fragment key={player.player_id}>
                <tr
                  className={`player-row ${player.is_suspension_risk ? 'suspension-risk' : ''} ${expandedPlayer === player.player_id ? 'expanded' : ''}`}
                  onClick={() => setExpandedPlayer(expandedPlayer === player.player_id ? null : player.player_id)}
                >
                  <td className="player-name-cell">
                    <span className={`player-team-indicator ${player.isHome ? 'home' : 'away'}`} />
                    <span className="player-name">{player.name}</span>
                    {player.starter_probability && player.starter_probability >= 0.8 && (
                      <span className="player-starter-badge" title="Likely starter">XI</span>
                    )}
                  </td>
                  <td className="player-pos">{player.position || '-'}</td>
                  <td className="player-risk">
                    <span
                      className="player-risk-score"
                      style={{ color: getRiskColor(player.card_risk_score) }}
                    >
                      {(player.card_risk_score * 100).toFixed(0)}%
                    </span>
                  </td>
                  <td className="player-stat">{player.yc_per_90.toFixed(2)}</td>
                  <td className="player-stat">{player.fouls_per_90?.toFixed(1) ?? '-'}</td>
                  <td className="player-suspension">
                    {player.is_suspension_risk ? (
                      <span className="suspension-warning" title={`${player.cards_until_suspension} cards until suspension`}>
                        <IoWarningOutline size={14} />
                        {player.cards_until_suspension}
                      </span>
                    ) : (
                      <span className="suspension-safe">-</span>
                    )}
                  </td>
                </tr>

                {/* Expanded Details */}
                {expandedPlayer === player.player_id && (
                  <tr className="player-expanded-row">
                    <td colSpan={6}>
                      <div className="player-expanded-content">
                        <div className="player-expanded-stats">
                          <div className="player-expanded-stat">
                            <span className="player-expanded-label">Tackles/90</span>
                            <span className="player-expanded-value">{player.tackles_per_90?.toFixed(1) ?? '-'}</span>
                          </div>
                          <div className="player-expanded-stat">
                            <span className="player-expanded-label">Season Yellows</span>
                            <span className="player-expanded-value">{player.season_yellows ?? '-'}</span>
                          </div>
                          <div className="player-expanded-stat">
                            <span className="player-expanded-label">Susp. Threshold</span>
                            <span className="player-expanded-value">{player.suspension_threshold}</span>
                          </div>
                          <div className="player-expanded-stat">
                            <span className="player-expanded-label">Starter Prob.</span>
                            <span className="player-expanded-value">
                              {player.starter_probability ? `${(player.starter_probability * 100).toFixed(0)}%` : '-'}
                            </span>
                          </div>
                        </div>

                        {/* Likely Opponent */}
                        {player.likely_opponent && (
                          <div className="player-matchup">
                            <span className="player-matchup-label">Likely matchup:</span>
                            <span className="player-matchup-name">{player.likely_opponent.player_name}</span>
                            {player.likely_opponent.fouls_drawn_per_90 != null && (
                              <span className="player-matchup-stat">
                                ({player.likely_opponent.fouls_drawn_per_90.toFixed(1)} fouls drawn/90)
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Top Risk Players Summary */}
      <div className="player-top-risks">
        <h4 className="cards-section-title">Highest Card Risk</h4>
        <div className="player-top-risks-list">
          {sortedPlayers
            .sort((a, b) => b.card_risk_score - a.card_risk_score)
            .slice(0, 5)
            .map((player, i) => (
              <div key={player.player_id} className="player-top-risk-item">
                <span className="player-top-risk-rank">{i + 1}</span>
                <span className={`player-team-indicator ${player.isHome ? 'home' : 'away'}`} />
                <span className="player-top-risk-name">{player.name}</span>
                <span
                  className="player-top-risk-score"
                  style={{ color: getRiskColor(player.card_risk_score) }}
                >
                  {(player.card_risk_score * 100).toFixed(0)}%
                </span>
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}
